import React, { useEffect, useState } from 'react'
import convert from 'convert-units'

import { getChallengeResponsesForChallenge } from '@src/pages/workflow/settings/analytics.resource'
import { toastService } from '@utils/toast.service'
import { WorkflowLeaderboard } from '@src/pages/workflow/settings/challenge-items/workflow-leaderboard.component'
import { lengthOptions } from '@src/pages/workflow/fields.helper'
import { PillSelect } from '@components/pill/pill-select.component'
import { sumDistances } from '@src/pages/workflow/settings/progress.helpers'

type DistanceValue = {
  value: number
  unit: string
}

type WorkflowLeaderboardItemDistanceProps = {
  isAdmin: boolean
  workflowChallenge: WorkflowChallenge
  onEdit: (challenge: WorkflowChallenge) => void
  onDelete: (id: string) => void
  participantsCount: number
}

export function WorkflowLeaderboardItemDistance({
  isAdmin,
  workflowChallenge,
  onEdit,
  onDelete,
  participantsCount,
}: WorkflowLeaderboardItemDistanceProps) {
  const [responses, setResponses] = useState<any[]>([])
  const [selectedLengthUnit, setSelectedLengthUnit] = useState(
    lengthOptions.find(
      lengthOption => lengthOption.id === workflowChallenge.goalUnit,
    ),
  )

  useEffect(() => {
    getChallengeResponsesForChallenge(workflowChallenge as any)
      .then(setResponses)
      .catch(err => {
        console.log(err)
        toastService.error(
          'There was an error retrieving challenge data. Please try again later.',
        )
      })
  }, [])

  function convertDistance(response: DistanceValue) {
    const convertedNum = convert(response.value)
      .from(response.unit as any)
      .to(selectedLengthUnit!.id as any)
    return { value: Math.round(convertedNum * 10) / 10, unit: response.unit }
  }

  return (
    <WorkflowLeaderboard
      workflowChallenge={workflowChallenge}
      responses={responses}
      selectedUnit={selectedLengthUnit}
      processValue={convertDistance}
      processMethods={(methods: any) => {
        return {
          ...methods,
          sum({ values }: { values: DistanceValue[] }) {
            return {
              value: sumDistances(values, selectedLengthUnit.id),
              unit: selectedLengthUnit.id,
            }
          },
          average({ values }: { values: DistanceValue[] }) {
            return {
              value:
                sumDistances(values, selectedLengthUnit.id) / values.length,
              unit: selectedLengthUnit.id,
            }
          },
          maxValue({ values }: { values: DistanceValue[] }) {
            const convertedValues = values.map(({ value, unit }) =>
              convert(Number(value)).from(unit as any).to('mm'),
            )
            const max = Math.max(...convertedValues)
            const maxIndex = convertedValues.indexOf(max)
            return values[maxIndex]
          },
          minValue({ values }: { values: DistanceValue[] }) {
            const convertedValues = values.map(({ value, unit }) =>
              convert(Number(value)).from(unit as any).to('mm'),
            )
            const min = Math.min(...convertedValues)
            const minIndex = convertedValues.indexOf(min)
            return values[minIndex]
          },
        }
      }}
    >
      <WorkflowLeaderboard.Header
        canEdit={isAdmin}
        onEdit={onEdit}
        onDelete={onDelete}
        participantsCount={participantsCount}
        displayValue={(value: DistanceValue) => `${value.value} ${selectedLengthUnit!.id}`}
        renderPills={() => [
          <PillSelect
            key={0}
            className="ml-1"
            data={lengthOptions}
            value={selectedLengthUnit as any}
            onChange={setSelectedLengthUnit as any}
          />,
        ]}
      />
      <WorkflowLeaderboard.Body />
    </WorkflowLeaderboard>
  )
}
